import React from 'react';
import { useInView } from 'react-intersection-observer';

import classNames from 'classnames';

import { GridTable, GridTableHeaderItem, Spinner, Tooltip } from '@uikit';

import { CampaignLeaderboardItem, SelfCampaignLeaderboardMe } from '@api';

import { CircleEmail } from '@uikit/icons/CircleEmail';
import { TelegramCircle2 } from '@uikit/icons/TelegramCircle2';

import truncate from '@shared/helpers/web3/truncate';

import { CampaignLeaderboardViewProps } from './CampaignLeaderboard';

const Socials = ({ className, me }: { className?: string; me: SelfCampaignLeaderboardMe }) => {
  const socials = [
    {
      id: 'email',
      Icon: CircleEmail,
      hidden: !me.attributes.email,
      tooltip: me.attributes.email,
    },
    {
      id: 'telegram',
      Icon: TelegramCircle2,
      hidden: !me.attributes.telegramId,
      tooltip: `id: ${me.attributes.telegramId}`,
    },
  ].filter((social) => !social.hidden);

  if (!socials.length) {
    return null;
  }

  return (
    <ul className={classNames('flex items-center', className)}>
      {socials.map(({ id, Icon, tooltip }) => {
        return (
          <li key={id}>
            <Tooltip
              content={tooltip}
              trigger={
                <Icon className="icon text-neutral-500 hover:text-primary-700 transition-colors cursor-pointer" />
              }
            />
          </li>
        );
      })}
    </ul>
  );
};

export const PointsTag = ({ xp, className }: { xp: string | number; className?: string }) => {
  return (
    <span
      className={classNames(
        'inline-flex justify-center items-center text-xs rounded-full px-2 py-1 min-h-[1.5rem] bg-primary-100',
        className,
      )}
    >
      {xp}
    </span>
  );
};

const tableHeader: GridTableHeaderItem<
  CampaignLeaderboardItem,
  { self?: SelfCampaignLeaderboardMe }
>[] = [
  {
    label: 'Rank',
    name: 'rank',
    render: ({ item }) => {
      return `#${item.attributes.rank}`;
    },
  },
  {
    label: 'Address',
    name: 'address',
    render: ({ item, meta }) => {
      const isSelf = item.id === meta?.self?.id;
      const evmAddress = item.attributes.evmWallet;
      const tonAddress = item.attributes.tonWallet;
      const formattedEvmAddress = evmAddress ? truncate(evmAddress) : null;
      const formattedTonAddress = tonAddress ? truncate(tonAddress, { start: 4 }) : null;
      const hasEvmAndTon = Boolean(evmAddress) && Boolean(tonAddress);

      const showSocials = !!meta?.self && isSelf;

      return (
        <div className="flex justify-between gap-3 max-md:flex-col">
          <span
            className={classNames('flex-grow flex-wrap flex', {
              'flex-col gap-1': hasEvmAndTon,
            })}
          >
            {!evmAddress && !tonAddress && '—'}

            {Boolean(evmAddress) && <span>{formattedEvmAddress}</span>}

            {Boolean(tonAddress) && <span>{formattedTonAddress}</span>}
          </span>

          {showSocials && <Socials className="gap-4" me={meta.self!} />}
        </div>
      );
    },
  },
  {
    label: 'Points',
    name: 'points',
    cellClassName: 'max-md:text-right',
    render: ({ item }) => {
      return <PointsTag className="whitespace-nowrap" xp={item.attributes.points} />;
    },
  },
];

const CampaignLeaderboardTable = ({
  className,
  query,
  me,
  items,
  id,
}: CampaignLeaderboardViewProps) => {
  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  const { ref } = useInView({
    onChange: (inView) => inView && !isLoading && !isFetchingNextPage && fetchNextPage(),
  });

  const showEmpty = !isLoading && !items?.length;
  const showLoader = isLoading || hasNextPage;

  return (
    <GridTable
      id={id}
      header={tableHeader}
      className={classNames(
        'overflow-y-auto flex flex-col rounded-t-2xl bg-neutral-50 shadow-card max-h-80 min-h-60',
        className,
      )}
      meta={{
        self: me,
      }}
      bodyRowClassName={({ item }) => {
        const isSelf = item.id === me?.id;

        return classNames('border-b flex-shrink-0 border-primary-300 min-h-14', {
          'sticky top-11 bg-primary-50 -order-1 max-md:py-3 max-md:items-baseline': isSelf,
        });
      }}
      headRowClassName="bg-primary-200 min-h-11 text-neutral-500 sticky -order-1 top-0"
      rowClassName="gap-2 px-2 md:gap-4 md:px-4 grid-cols-[0.6fr_minmax(60px,1fr)_100px] items-center"
      data={items}
    >
      {showEmpty && (
        <p className="m-auto text-center text-neutral-200 text-sm">Leaderboard is Empty</p>
      )}

      {showLoader && (
        // Key is added to trigger in view cb again if component is already in view
        <div ref={ref} key={data?.pages.length} className="m-auto py-3">
          <Spinner size="md" />
        </div>
      )}
    </GridTable>
  );
};

export default CampaignLeaderboardTable;
